/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import type { CalendarDate } from '@internationalized/date';
import forEach from 'lodash/forEach';
import { calendarDateToUtcString } from 'sarsaparilla';

import type { NightMap, Selection, Selections } from '../../../types/camping';
import { UTCStringToCalendarDate } from './dateUtils';

export const getNightsFromRange = (startDate: CalendarDate, endDate: CalendarDate) => {
    const nights = [];
    let cloneStartDate = startDate;
    do {
        nights.push(calendarDateToUtcString(cloneStartDate) as string);
        cloneStartDate = cloneStartDate.add({ days: 1 });
    } while (endDate.compare(cloneStartDate) > 0);

    return nights;
};

export const getNightMapFromSelection = (selection: Selection, campsiteId: string) => {
    const nightMap: Record<string, any> = {};
    const startDate = UTCStringToCalendarDate(selection.startDate);
    const endDate = UTCStringToCalendarDate(selection.actualEndDate || selection.endDate);

    const nights = getNightsFromRange(startDate, endDate);

    forEach(nights, (night) => {
        nightMap[night] = {
            campsite_id: campsiteId,
            campsite_loop: selection.loop || '',
            campsite_name: selection.name || '',
        };
    });

    return nightMap;
};

export const getItineraryNightMap = (selections: Selections) => {
    let nightMap: Record<string, any> = {};
    forEach(selections, (selection, campsiteId) => {
        // If there is a dateRange map (always true for ITINERARY_SINGLE)
        if (selection.dateRangeMap) {
            // For each date range in the current campsite's selection, construct night map with range start/end...
            forEach(selection.dateRangeMap, (end, start) => {
                const startDate = UTCStringToCalendarDate(start);
                const endDate = UTCStringToCalendarDate(end).subtract({ days: 1 });
                const nights = getNightsFromRange(startDate, endDate);
                const currentNightMap: Record<string, any> = {};

                forEach(nights, (night) => {
                    currentNightMap[night] = {
                        campsite_id: campsiteId,
                        campsite_loop: selection.loop || '',
                        campsite_name: selection.name || '',
                    };
                });
                nightMap = {
                    ...nightMap,
                    ...currentNightMap,
                };
            });
        }
    });
    return nightMap;
};

export const convertNightMapToItinerary = (nightMap: NightMap) => {
    const itinerary: Omit<Selection, 'actualEndDate'>[] = [];
    const nightMapKeys = Object.keys(nightMap);

    // nightMap may not be in order, so we sort it
    nightMapKeys.sort((a: string, b: string) => {
        return new Date(a).getTime() - new Date(b).getTime();
    });

    // Convert nightMap into an actual itinerary
    nightMapKeys.forEach((nightMapKey) => {
        const campsiteId = nightMap[nightMapKey].campsite_id;
        const night = UTCStringToCalendarDate(nightMapKey);
        const nightAfter = night.add({ days: 1 });
        const lastItineraryItem = itinerary[itinerary.length - 1];

        // If previous night was at the same campsite, update previous
        // itinerary item's end date
        if (lastItineraryItem && lastItineraryItem.campsiteId === campsiteId) {
            lastItineraryItem.endDate = calendarDateToUtcString(nightAfter) || '';
        } else {
            // is new campsite; push itinerary item to array
            itinerary.push({
                campsiteId,
                name: nightMap[nightMapKey].campsite_name,
                loop: nightMap[nightMapKey].campsite_loop,
                startDate: calendarDateToUtcString(night) || '',
                endDate: calendarDateToUtcString(nightAfter) || '',
            });
        }
    });

    return itinerary;
};
